// Global Instructions Rule Applied!

import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Button, Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import { DownOutlined, PlusOutlined } from '@ant-design/icons'
import { useTabManagerContext } from '../../core/app-layout/lib/TabManagerContext.tsx'
import type { RootState } from '../../state_manager/store.ts'
import { getReportingGridColumnsForReportId } from '../../state_manager/utils/reportingGridColumns.ts'
import { useReportingGridColumnsWithPreferences } from '../../state_manager/hooks/useReportingGridColumnsWithPreferences.ts'
import { getAddColumnsCatalogForReport } from '../../state_manager/utils/reportingColumnCatalog.ts'
import type { ViewConfigColumn } from '../../core/components/ViewConfig/types.ts'
import { useFundTransactionsData } from './hooks/useFundTransactionsData.ts'
import { useFundTransactionsReportId } from '../../state_manager/hooks/useFundTransactionsReportId.ts'
import { FundTransactionsTable } from './components/FundTransactionsTable.tsx'
import ViewToolbar from '../../core/components/ViewToolbar/index.tsx'
import { RIBBON_CTA_CLASS } from '../../core/components/ViewToolbar/index.tsx'
import { ViewConfig } from '../../core/components/ViewConfig/index.ts'
import { fetchAllFundTransactionsForExport } from '../shared/table_grid_export/fetchAllFilteredForExport.ts'
import { useReportingGridExport } from '../shared/table_grid_export/useReportingGridExport.ts'
import { TABLE_CAPITAL_CALLS, TABLE_DISTRIBUTIONS, TABLE_FUND_TRANSACTIONS } from '../../state_manager/globals.ts'

export default function FundTransactionsPage() {
  const reportingConfigs = useSelector((state: RootState) => state.globalConfigs?.reporting_configs ?? [])
  const transactionTypes = useSelector((state: RootState) => state.globalConfigs?.fund_transaction_types ?? [])
  const reportingColumnDefaults = useSelector(
    (state: RootState) => state.globalConfigs?.reporting_column_defaults ?? []
  )
  const reportingColumns = useSelector((state: RootState) => state.globalConfigs?.reporting_columns ?? [])

  const reportId = useFundTransactionsReportId()
  const { openFormInTab, openModuleTab } = useTabManagerContext()

  const {
    data,
    dataLoading,
    activeFilters,
    activeSorting,
    preferenceRow,
    hasMore,
    currentPage,
    loadingMore,
    filterOptions,
    filterOptionsLoading,
    handleFetch,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    handleLoadMore,
    defaultTableFilters,
    defaultTableSorting,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault
  } = useFundTransactionsData()

  const gridColumns = useReportingGridColumnsWithPreferences(reportId, preferenceRow)

  const fetchAllForExport = useCallback(
    (token: string | null) => fetchAllFundTransactionsForExport(activeSorting, activeFilters, token),
    [activeSorting, activeFilters]
  )
  const { exportLoading, handleExport } = useReportingGridExport({
    title: 'Fund Transactions',
    gridColumns,
    fetchAll: fetchAllForExport
  })

  // Fund Transactions is the hub for fund actions (owner 2026-09-24): one
  // "+ New" menu creates a Transaction / Capital call / Closing and links to
  // the Fee booking workbench and the cross-fund Capital calls grid, whose
  // menu items are hidden. The Closing entry is the cfg type flagged
  // closes_commitments; Transfer of interest the one flagged
  // transfers_interest.
  const closingType = useMemo(() => transactionTypes.find((t) => t.closes_commitments) ?? null, [transactionTypes])
  const transferType = useMemo(() => transactionTypes.find((t) => t.transfers_interest) ?? null, [transactionTypes])
  const newMenuItems: MenuProps['items'] = useMemo(
    () => [
      { key: 'transaction', label: 'Transaction' },
      { key: 'capital-call', label: 'Capital call' },
      { key: 'distribution', label: 'Distribution' },
      { key: 'closing', label: 'Closing', disabled: !closingType },
      { key: 'transfer', label: 'Transfer of interest', disabled: !transferType },
      { type: 'divider' },
      { key: 'fee-booking', label: 'Fee booking workbench' },
      { key: 'capital-calls', label: 'All capital calls' }
    ],
    [closingType, transferType]
  )
  const onNewMenuClick: NonNullable<MenuProps['onClick']> = useCallback(
    ({ key }) => {
      if (key === 'transaction') openFormInTab(TABLE_FUND_TRANSACTIONS, null)
      else if (key === 'capital-call') openFormInTab(TABLE_CAPITAL_CALLS, null, 'New Capital Call')
      else if (key === 'distribution') openFormInTab(TABLE_DISTRIBUTIONS, null, 'New Distribution')
      else if (key === 'closing' && closingType)
        openFormInTab(TABLE_FUND_TRANSACTIONS, null, 'Closing', { type: String(closingType.id) })
      else if (key === 'transfer' && transferType)
        openFormInTab(TABLE_FUND_TRANSACTIONS, null, 'Transfer of interest', { type: String(transferType.id) })
      else if (key === 'fee-booking') openModuleTab('fee-booking')
      else if (key === 'capital-calls') openModuleTab('capital-calls')
    },
    [openFormInTab, openModuleTab, closingType, transferType]
  )

  const addColumnsCatalog = useMemo(
    () =>
      getAddColumnsCatalogForReport(
        reportId,
        {
          reporting_columns: reportingColumns,
          reporting_column_defaults: reportingColumnDefaults
        },
        { primaryModuleKey: 'fund_transactions' }
      ),
    [reportId, reportingColumns, reportingColumnDefaults]
  )

  const catalogFieldNameOrder = useMemo(
    () => [...addColumnsCatalog.deal, ...addColumnsCatalog.related].map((c) => c.valueKey),
    [addColumnsCatalog]
  )

  const defaultTableColumnKeys = useMemo(() => {
    if (!reportId) return null
    const rows = getReportingGridColumnsForReportId(reportId, {
      reporting_configs: reportingConfigs,
      reporting_column_defaults: reportingColumnDefaults,
      reporting_columns: reportingColumns
    })
    return rows.map((r) => r.valueKey)
  }, [reportId, reportingConfigs, reportingColumnDefaults, reportingColumns])

  const columnByValueKey = useMemo(() => {
    const m = new Map<string, ViewConfigColumn>()
    for (const c of addColumnsCatalog.deal) m.set(c.valueKey, c)
    for (const c of addColumnsCatalog.related) m.set(c.valueKey, c)
    for (const c of gridColumns) {
      if (!m.has(c.valueKey)) m.set(c.valueKey, c)
    }
    return m
  }, [addColumnsCatalog, gridColumns])

  const resolveViewConfigColumn = useCallback(
    (valueKey: string) => columnByValueKey.get(valueKey),
    [columnByValueKey]
  )

  const columnLayout = useMemo(
    () => ({
      onPersistFieldOrder: persistColumnFieldOrder,
      onResetToDefaults: resetColumnLayoutToDefault,
      resolveColumn: resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    }),
    [
      persistColumnFieldOrder,
      resetColumnLayoutToDefault,
      resolveViewConfigColumn,
      catalogFieldNameOrder,
      defaultTableColumnKeys
    ]
  )

  return (
    <div className='h-full overflow-hidden'>
      <div className='w-full h-full flex flex-col bg-white px-2 sm:px-4 pb-2 sm:pb-4 overflow-hidden'>
        <ViewToolbar
          moduleKey={TABLE_FUND_TRANSACTIONS}
          searchEnabled={false}
          addButtonEnabled={false}
          activeFilters={activeFilters}
          onRefreshData={(filters) => handleFetch(activeSorting, Array.isArray(filters) ? filters : activeFilters)}
          onFilterChange={(filters) => handleSetFilters(Array.isArray(filters) ? filters : [])}
          defaultFilters={defaultTableFilters}
          onResetFilters={handleResetFilters}
          exportEnabled
          onExport={handleExport}
          exportLoading={exportLoading}
          beforeAddSlot={
            <>
            <ViewConfig
              moduleTitle='Fund Transactions'
              columns={gridColumns}
              filterOptions={filterOptions}
              filterOptionsLoading={filterOptionsLoading}
              addColumnsCatalog={addColumnsCatalog}
              addColumnsTabLabels={{ primary: 'Transaction', related: 'Related' }}
              columnLayout={columnLayout}
              filteringConfig={{
                currentFilters: activeFilters,
                defaultFilters: defaultTableFilters,
                onApplyFilters: async (filters) => {
                  handleSetFilters(filters)
                },
                onResetToDefaults: async () => {
                  handleSetFilters(defaultTableFilters)
                }
              }}
              sortingConfig={{
                currentSorting: activeSorting,
                defaultSorting: defaultTableSorting,
                onApplySorting: async (sorting) => {
                  handleSetSorting(sorting)
                },
                onResetToDefaults: async () => {
                  handleSetSorting(defaultTableSorting)
                }
              }}
            />
            {/* Booking screens are full forms (no drawer), so the stock
                AddButton's drawer route doesn't apply - the menu opens form
                tabs. Single ribbon control by design (hub). */}
            <Dropdown trigger={['click']} menu={{ items: newMenuItems, onClick: onNewMenuClick }}>
              <Button type='primary' icon={<PlusOutlined />} className={RIBBON_CTA_CLASS}>
                New <DownOutlined />
              </Button>
            </Dropdown>
            </>
          }
        />

        <div className='flex-1 min-h-0 h-full flex flex-col'>
          <FundTransactionsTable
            data={data}
            loading={dataLoading}
            loadingMore={loadingMore}
            hasMore={hasMore}
            currentPage={currentPage}
            gridColumns={gridColumns}
            activeFilters={activeFilters}
            activeSorting={activeSorting}
            filterOptions={filterOptions}
            filterOptionsLoading={filterOptionsLoading}
            onLoadMore={handleLoadMore}
            onFiltersChange={(filters) => {
              const filterArray = Object.entries(filters).map(([key, value]) => ({ key, value }))
              handleSetFilters(filterArray)
            }}
            onSortChange={(sortConfig: any) => {
              if (sortConfig) {
                handleSetSorting([{ key: sortConfig.column, direction: sortConfig.direction }])
              } else {
                handleSetSorting([])
              }
            }}
          />
        </div>
      </div>
    </div>
  )
}
